import { OperatingSystem, Severity } from '../enums';
import { Partner } from './partner';
import { Customer } from './customer';
import { Runner } from './runner';
import { BoxType, getBoxTypeLabel, isVmBox } from './box.utils';

export type { BoxType };

export interface AdminBoxUpdateRequestDto {
  displayName?: string | null;
  partnerId?: string | null;
  customerId?: number | null;
}

export interface Box {
  id: string;
  displayName: string;
  name: string;
  type?: BoxType;
  os: OperatingSystem | string;
  customer?: Customer;
  manager?: Partner;
  runner?: Runner;
  connected: boolean;
  ipv4: string[];
  version: string;
  timezone: string;
  status: Severity;
}

export namespace Box {
  export function isAssigned(box: Box): boolean {
    return box.customer != null || box.manager != null;
  }

  export function getTypeName(box?: Box | null, translateService?: { instant: (key: string) => string }): string {
    if (!box) {
      return '-';
    }

    if (box.type) {
      return getBoxTypeLabel(box.type, translateService as any);
    }

    if (typeof box.os === 'string') {
      const normalized = box.os.toUpperCase();
      if (normalized.includes('HYPERV') || normalized.includes('HYPER-V')) {
        return getBoxTypeLabel('HYPERV', translateService as any);
      }
      if (normalized.includes('VMWARE')) {
        return getBoxTypeLabel('VMWARE', translateService as any);
      }
      if (normalized.includes('VIRTUALBOX')) {
        return getBoxTypeLabel('VIRTUALBOX', translateService as any);
      }
      if (normalized.includes('HARDWARE') || normalized.includes('PHYSISCH') || normalized.includes('PHYSICAL')) {
        return getBoxTypeLabel('PHYSICAL', translateService as any);
      }
      return box.os || '-';
    }

    return '-';
  }

  export function mapFromApiValue(box: any): Box {
    const { osName, manager, customer, ...restOfBox } = box;
    let boxType: BoxType = box.type;
    if (!boxType) {
      if (box.osName) {
        const osStr = String(box.osName).toUpperCase();
        if (osStr.includes('HYPERV') || osStr.includes('HYPER-V')) {
          boxType = 'HYPERV';
        } else if (osStr.includes('VMWARE')) {
          boxType = 'VMWARE';
        } else if (osStr.includes('VIRTUALBOX')) {
          boxType = 'VIRTUALBOX';
        } else {
          boxType = 'PHYSICAL';
        }
      } else {
        boxType = 'PHYSICAL';
      }
    }
    return {
      ...restOfBox,
      type: boxType,
      os: osName ? OperatingSystem.mapFromApiValue(osName) : [],
      manager: manager && Partner.mapFromApiValue(manager),
      customer: customer
        ? {
            ...customer,
            companyName: customer.companyName ?? customer.name,
          }
        : undefined,
      status: undefined,
    };
  }
}

export * from './box.utils';
